import React, { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KTIcon } from '../../../../_myb/helpers';
import { ToolbarWrapper } from '../../../../_myb/layout/components/toolbar';
import {
  ChartsWidget1,
  ListsWidget5,
  TablesWidget1,
  TablesWidget5,
} from '../../../../_myb/partials/widgets';
import { Content } from '../../../../_myb/layout/components/content';
import SearchablePicker from '../../widgets/components/SearchablePicker';
import { fetchAccounts, fetchFieldConfig, fetchFile, fetchItem, fetchUoms, upSertItem } from '../../../api';

import PictureUploadBox from '../../widgets/components/PictureUploadBox';
import { CustomPrompt } from '../../widgets/components/ModalPrompts';
import SliderSwitch from '../../widgets/components/SliderSwitch';
import '../../widgets/components/Layout.css';
import { CategoryPicker } from './CategoryPicker';
import NumberInput from '../../widgets/components/NumberInput';
import { AttrType, Contact, ContactType, FieldConfig, Item, ItemTyp, SubItems, TaxCalcType } from '../../../types';
import { toast } from 'react-toastify';
import { scrollToFirstError } from '../../utils/MiscFunctions';
import { UomPicker } from './Uom';
import { AccountPicker } from './AccountPicker';

import LabelWithTooltip from '../../widgets/components/CustomLabel';

import { ToolbarContext, defaultToolbarState } from '../../../../_myb/layout/components/toolbar/Toolbar';
import { TaxPicker } from './TaxPicker';
import CustomModal from '../../widgets/components/CustomModal';
import { AddFieldConfigurator, EditFieldConfigurator } from '../../widgets/components/FieldConfigs';
import { useAppContext } from '../../apps/AppContext';
import { ContactPicker } from './ContactPicker';
import ButtonWithLoader from '../../widgets/components/ButtonWithLoader';
import { AccountCategory, AccountType } from '../../../Constants';
import DatePicker from '../../widgets/components/DatePicker';
import { EasyPicker } from '../../widgets/components/SimplePicker';
import { EditableBundledItems } from './BundledItems';
import { ItemPicker } from './ItemPicker';
import { trackEvent, AnalyticsEvent } from '../../apps/analytics';

export interface UOM {
  uomId: string;
  uomNm: string;
}

interface OptionType {
  value: string;
  label: string;
}

const ITEM_TYPE_OPTIONS = ['Product', 'Service', 'Bundle'];

interface ItemFormProps {
  onModal?: boolean
  onSave?: (item: Item) => void
  onExit?: () => void  // Add this line
}

export function ItemForm({ onModal, onSave, onExit }: ItemFormProps) {
  const { itemId } = useParams<{ itemId: string }>();
  const { updateToolbar } = useContext(ToolbarContext);
  const navigate = useNavigate();
  const { bizMaster, bizSettings } = useAppContext();
  const isCIN = (bizMaster?.bizDetails?.country === 'IN')
  const isCNP = (bizMaster?.bizDetails?.country === 'NP')
  const [uoms, setUoms] = useState<OptionType[]>([]);
  const [salesAccounts, setSalesAccounts] = useState<OptionType[]>([]);
  const [cogsAccounts, setCogsAccounts] = useState<OptionType[]>([]);
  const [item, setItem] = useState<Item>(new Item());
  const [uomTrigger, setUomTrigger] = useState(false);
  const [isInventoryEnabled, setIsInventoryEnabled] = useState(bizSettings?.inventoryFlag !== false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [itemImages, setItemImages] = useState<string[]>([]);
  const [fieldConfig, setFieldConfigs] = useState<FieldConfig[]>([])
  const [showAddFieldModal, setShowAddFieldModal] = useState(false)
  const [showEditFieldModal, setShowEditFieldModal] = useState(false)
  const [file, setFile] = useState<File | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const itemNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const value =
      bizSettings?.inventoryFlag === false ||
        item.inventoryFlag === false
        ? false
        : true;

    setIsInventoryEnabled(value);
  }, [bizSettings?.inventoryFlag, item.inventoryFlag]);



  useEffect(() => {
    updateToolbar({
      ...defaultToolbarState,
      alwaysDisplay: true,
    });
    // Focus on the contact name input field when the component mounts
    if (itemNameRef.current) {
      itemNameRef.current.focus();
    }
    const getItem = async () => {
      if (itemId) {
        const { item, files } = await fetchItem(itemId)
        // Process image files
        if (files && Array.isArray(files)) {
          const imageUrls = files.map(itemImgFile => {
            try {
              if (itemImgFile instanceof Blob && itemImgFile.type.startsWith('image/')) {
                // If it's a Blob or File and it's an image, create an object URL

                return URL.createObjectURL(itemImgFile);
              } else {
                console.error('Invalid file type for createObjectURL or unsupported content:', itemImgFile);
                return null;
              }
            } catch (err) {
              console.error('Error creating object URL for image:', err);
              return null;
            }
          }).filter((url): url is string => url !== null); // Filter out any null URLs and ensure TypeScript treats the array as string[]

          setItemImages(imageUrls); // Set the image URLs
        }
        setItem(item)
      }
    }
    const getUoms = async () => {
      try {
        const fetchedUoms = await fetchUoms();
        const options = fetchedUoms.map((uom) => ({
          value: uom.uomId,
          label: uom.uomNm,
        }));
        setUoms(options);
      } catch (error) {
        console.error('Error fetching UOMs:', error);
      }
    };
    const getAccounts = async () => {
      try {
        const fetchedAccounts = await fetchAccounts();
        const salesOptions = fetchedAccounts
          .filter((account) => account.accountType === 'Income' && account.accountId !== undefined)
          .map((account) => ({
            value: account.accountId as string,  // Ensure accountId is a string
            label: account.accountName,
          }));
        setSalesAccounts(salesOptions);
        const cogsOptions = fetchedAccounts
          .filter((account) => account.accountType === AccountType.Expense)
          .map((account) => ({
            value: account.accountId as string,
            label: account.accountName,
          }));
        setCogsAccounts(cogsOptions);
      } catch (error) {
        console.error('Error fetching sales accounts:', error);
      }
    };

    getUoms();
    getAccounts();
    getItem()
  }, [uomTrigger]);



  useEffect(()=>{
    const getFieldConfig = async () => {
      try {
        const fieldConfigResult = await fetchFieldConfig('Item')
        setFieldConfigs(fieldConfigResult)
      } catch (error) {
        console.error('Error fetching Field Configs', error);
      }
    }
      getFieldConfig()
    },[showAddFieldModal,showEditFieldModal])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isFormDirty) {
        event.preventDefault();
        event.returnValue = ''; // This line is necessary for some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement> | { target: { name: string; type: string; value: string; checked: boolean } }
  ) => {

    const { name, type, value, checked } = event.target;
    setItem((prevItem: any) => {
      const updatedItem = { ...prevItem };

      // Only copy itemRate to salesRate if salesRate is undefined
      if (name === 'itemRate') {
        // const parsedValue = parseFloat(value); // Ensure it's treated as a number
        updatedItem.salesRate = value;
      }

      // Always update the itemRate field, but salesRate should only be set initially
      return {
        ...updatedItem,
        [name]: type === 'checkbox' ? checked : (name === 'itemRate' ? value : value),
      };
    });

    setIsFormDirty(true);
  };

  const handlePickerChange = (name: string, selectedOption: OptionType | null) => {
    setItem((prevItem: any) => ({ ...prevItem, [name]: selectedOption?.value || '' }));
    setIsFormDirty(true);
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setIsInventoryEnabled(checked);
    setItem((prevItem: any) => ({
      ...prevItem,
      inventoryFlag: checked
    }))
    setIsFormDirty(true);
  };

  useEffect(() => {
    const isBundleOrService =
      item.itemTyp === ItemTyp.Bundle || item.itemTyp === ItemTyp.Service;
    // If it's bundle/service → inventory must be false
    if (isBundleOrService) {
      setItem(prev => ({
        ...prev,
        inventoryFlag: false,
        subItems:
          item.itemTyp === ItemTyp.Bundle && !item.subItems
            ? [new SubItems()]
            : item.subItems,
      }));
      return;
    }

    // Normal items → true unless explicitly false
    const inventoryStatus =
      item.inventoryFlag === false ? false : true;
    setItem(prev => ({
      ...prev,
      inventoryFlag: inventoryStatus,
      subItems:
        item.itemTyp === ItemTyp.Bundle && !item.subItems
          ? [new SubItems()]
          : item.subItems,
    }));
  }, [item.itemTyp]);



  const handleSubItemChange = (index: number, updatedSubItem: Partial<SubItems>) => {
    setItem((prevItem: any) => ({
      ...prevItem,
      subItems: prevItem.subItems.map((subItem: SubItems, i: number) =>
        i === index ? { ...subItem, ...updatedSubItem } : subItem
      ),
    }));
  };

  const handleDeleteRow = (index: number) => {
    setItem((prevItem: any) => ({
      ...prevItem,
      subItems: prevItem.subItems.filter((_: any, i: number) => i !== index),
    }));
  };

  const handleAddBundleItemRow = () => {
    setItem((prevItem: any) => ({
      ...prevItem,
      subItems: [...(prevItem.subItems || []), new SubItems()] // Create a new array with the added item
    }));
  };
  const [errors, setErrors] = useState<{ itemName: string }>({ itemName: '' });

  const [isPromptVisible, setPromptVisible] = useState(false);

  const doExit = () => {
    if (!onModal) {
      navigate(-1);
    } else if (onExit) {
      onExit();
    }
  };

  const handleExit = () => {
    if (isFormDirty) {
      setPromptVisible(true);
      return false;
    }
    doExit();
    return true;
  };

  const handleConfirmExit = () => {
    setPromptVisible(false);
    setIsFormDirty(false);
    doExit();
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    setIsLoading(true)
    if (!item.itemName || item.itemName.trim() === '') {
      setErrors({ itemName: 'Item Name cannot be empty'.toTranslatedDisplay() });
      setIsLoading(false);
      toast.error('Item Name cannot be empty'.toTranslatedDisplay());
      // MBAI-28: scroll to first invalid field
      scrollToFirstError('itemName');
      return;
    }

    try {
      const updatedItem = await upSertItem(item, file);
      if (!itemId) {
        trackEvent(AnalyticsEvent.ItemCreated);
      }
      setIsLoading(false)
      if (onSave) onSave(item)
      // Handle success, such as showing a success message or redirecting
      setIsFormDirty(false); // Mark form as not dirty after successful submit
      toast.success('Item saved successfully!'.toTranslatedDisplay())
      if (!onModal) navigate('/items/view-items');;
    } catch (error: any) {
      setIsLoading(false)
      toast.error('Error '.toTranslatedDisplay() + error.message)
      console.error('Error upserting item:', error);
    }
  };

  const [dateState, setDateState] = useState<any>({
    date1: new Date(),
    date2: new Date(),
  });

  const handleImageUpload = (image: File | undefined) => {
    if (image) {
      // Handle file upload
      setFile(image);
    } else {
      // Handle file deletion
      setFile(undefined); // or undefined, depending on your file state type

      // Set itemImages to null in the main item state object
      setItem(prevItem => ({
        ...prevItem,
        itemImages: undefined
      }));

    }
  };

  function handleCustomFields(event: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = event.target;

    setItem(prevItem => {
      const customFields = prevItem.customFields || [];

      // Update the customFields array by finding the matching field by fieldName
      const updatedFields = customFields.map(field =>
        field.fieldName === name ? { ...field, fieldValue: value } : field
      );

      // If the field does not exist, add a new one
      if (!customFields.some(field => field.fieldName === name)) {
        updatedFields.push({ fieldName: name, fieldValue: value });
      }

      return {
        ...prevItem,
        customFields: updatedFields,
      };
    });
  }


  function handleAddFieldModalClose(): void {
    setShowAddFieldModal(false)
  }

  function handleAddFieldModalToggle(): void {
    setShowAddFieldModal(!showAddFieldModal)
  }

  function handleEditFieldModalClose(): void {
    setShowEditFieldModal(false)
  }

  function handleEditFieldModalToggle(): void {
    setShowEditFieldModal(!showEditFieldModal)
  }

  const handleLockClick = () => {
    setItem(prevItem => ({
      ...prevItem,
      isRateLocked: !item.isRateLocked, // Update the isRateLocked property
    }));
  };

  return (
    <Content>
      <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header cursor-pointer'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>{'Item Details'.toTranslatedDisplay()}</h3>
          </div>
          <div className='card-toolbar d-flex align-items-center'>
            <div className='d-flex align-items-center me-5'>
              <SliderSwitch
                name='isActive'
                defaultChecked={item.isActive !== false}
                onChange={(name, checked) => handleInputChange({ target: { name, type: 'checkbox', value: '', checked } })}
              />
              <LabelWithTooltip
                className='ms-2'
                labelText={item.isActive !== false ? 'Active' : 'Archived'}
                tooltipText="Archived items are hidden from sales/purchase pickers but remain visible on past transactions and reports"
              />
            </div>
            <ButtonWithLoader shortcutTip='Ctrl+S / ⌘+S'
              isLoading={isLoading} onClick={handleSubmit} >
              {'Save'.toTranslatedDisplay()}
            </ButtonWithLoader>
            <button type='button' className='btn btn-secondary ms-2' onClick={handleExit} tabIndex={3}>{'Cancel'.toTranslatedDisplay()}</button>
          </div>
        </div>

        <div className='card-body p-9'>
          <div className='row mb-7'>
            <div className='col-md-9'>
              <div className='row mb-7'>
                <div className='col-md-4'>
                  <div className='mb-3'>
                    <label className='form-label'>{'Item Name'.toTranslatedDisplay()}</label>
                    <input
                      type='text'
                      className={`form-control ${errors.itemName ? 'is-invalid' : ''}`}
                      placeholder={'Item Name'.toTranslatedDisplay()}
                      name='itemName'
                      value={item.itemName || ''}
                      ref={itemNameRef}
                      onChange={handleInputChange}
                    />
                    {errors.itemName && <div className="invalid-feedback">{errors.itemName}</div>}
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='mb-3'>
                    <div className='d-flex justify-content-between'>
                      <label htmlFor="">{'Item Rate'.toTranslatedDisplay()}</label>
                      <div onClick={handleLockClick} className='cursor-pointer'>
                        {item.isRateLocked ? 'Locked'.toTranslatedDisplay() : 'Unlocked'.toTranslatedDisplay()} {'in Transactions'.toTranslatedDisplay()}
                        <i className={`ms-1 bi bi-${!item.isRateLocked ? 'unlock' : 'lock'}`}></i>
                      </div>
                    </div>
                    <NumberInput
                      name='itemRate'
                      value={item?.itemRate}
                      onChange={handleInputChange}
                      showCurrency
                    />
                  </div>
                </div>
                <div className='col-md-4'>
                  <EasyPicker label={'Item Type'.toTranslatedDisplay()} name='itemTyp' value={item.itemTyp}
                    onSelectionChange={handleInputChange}
                    singleValueOptions={ITEM_TYPE_OPTIONS} />
                </div>
                <div className='col-md-4 mb-3'>
                  <label className='form-label'>{'Item Description'.toTranslatedDisplay()}</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder={'Description'.toTranslatedDisplay()}
                    name='itemTxt'
                    value={item.itemTxt || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className='col-md-4'>
                  <div className='mb-3'>
                    <label className='form-label'>{'SKU/Item Code'.toTranslatedDisplay()}</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={'SKU/Item Code'.toTranslatedDisplay()}
                      name='itemCode'
                      value={item.itemCode || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className='col-md-4'>
                  <div className='App'>
                    <LabelWithTooltip
                      className='form-label'
                      labelText='Category'
                      tooltipText='Groups this item for filtering in reports and item lists. Purely organizational — does not affect tax or pricing.'
                    />
                    <CategoryPicker
                      selectedCategory={item.itemCategory}
                      onCategoryChange={(categoryId) => setItem((prevItem: any) => ({ ...prevItem, itemCategory: categoryId }))} categoryType={AttrType.ItemCategory} />
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='App'>
                    <LabelWithTooltip
                      className='form-label'
                      labelText='Item Group'
                      tooltipText='A second way to group items for price lists and reports. Manage groups under Settings. Purely organizational — does not affect tax or pricing.'
                    />
                    <CategoryPicker
                      selectedCategory={item.itemGroup}
                      onCategoryChange={(groupId) => setItem((prevItem: any) => ({ ...prevItem, itemGroup: groupId }))} categoryType={AttrType.ItemGroup} />
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='App'>
                    <label className='form-label'>{'UoM'.toTranslatedDisplay()}</label>
                    <UomPicker selectedUom={item.uomId} onUomChange={(uomId) => setItem((prevItem: any) => ({ ...prevItem, uomId: uomId }))} />
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='App'>
                    <label className='form-label'>{'Secondary UoM'.toTranslatedDisplay()} <span className='text-muted fw-normal fs-8'>{'(optional)'.toTranslatedDisplay()}</span></label>
                    <UomPicker selectedUom={item.secondaryUomId} onUomChange={(uomId) => setItem((prevItem: any) => ({ ...prevItem, secondaryUomId: uomId || undefined }))} />
                  </div>
                </div>
                {item.secondaryUomId && (
                  <div className='col-md-4'>
                    <NumberInput
                      label={`${'Conversion Factor'.toTranslatedDisplay()} (1 primary = X secondary)`}
                      tooltip='How many secondary units equal 1 primary unit. E.g. 1 KG = 1000 Grams → enter 1000'
                      name='conversionFactor'
                      value={item.conversionFactor}
                      onChange={handleInputChange}
                    />
                  </div>
                )}
                <div className='col-md-4'>
                  <label className='form-label'>{'Barcode'.toTranslatedDisplay()}</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder={'Barcode'.toTranslatedDisplay()}
                    name='barcode'
                    value={item.barcode || ''}
                    onChange={handleInputChange}
                  />
                </div>
                <div className='col-md-4'>
                  <NumberInput
                    label={'MRP'.toTranslatedDisplay()}
                    tooltip='Maximum retail price or Manufacture suggested retail price(Optional)'
                    name='mrp'
                    value={item.mrp}
                    onChange={handleInputChange}
                    showCurrency
                  />
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <PictureUploadBox onUpload={handleImageUpload} selectedImage={itemImages[0]} />
            </div>
          </div>

          <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 fw-bolder '>
            {(item.itemTyp !== ItemTyp.Bundle) ?
              <>
                <li className='nav-item'>
                  <a
                    className={`nav-link ${(item.itemTyp !== ItemTyp.Bundle) && 'active'} text-active-primary`}
                    data-bs-toggle='tab'
                    href='#kt_sales_purchase_settings'
                  >
                    {'Sales & Purchase Settings'.toTranslatedDisplay()}
                  </a>
                </li>
                {bizSettings?.taxFlag !== false &&
                  <li className='nav-item'>
                    <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {(isCIN) ? ' GST Settings'.toTranslatedDisplay() : 'Tax Settings'.toTranslatedDisplay()}
                    </a>
                  </li>
                }
                {bizSettings?.inventoryFlag !== false && item.itemTyp !== ItemTyp.Service &&
                  <li className='nav-item'>
                    <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                      {'Inventory'.toTranslatedDisplay()}
                    </a>
                  </li>
                }
              </> :
              <>
                <li className='nav-item'>
                  <a className={`nav-link ${(item.itemTyp === ItemTyp.Bundle) && 'active'}  text-active-primary`} data-bs-toggle='tab' href='#kt_tab_pane_5'>
                    {'Bundle Items'.toTranslatedDisplay()}
                  </a>
                </li>
                {bizSettings?.taxFlag !== false &&
                  <li className='nav-item'>
                    <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {(isCIN) ? ' GST Settings'.toTranslatedDisplay() : 'Tax Settings'.toTranslatedDisplay()}
                    </a>
                  </li>
                }
              </>
            }
            <li className='nav-item'>
              <a className='nav-link text-active-primary' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                {'Custom Fields'.toTranslatedDisplay()}
              </a>
            </li>
          </ul>

          <div className='tab-content' id='myTabContent'>
            <div className={`tab-pane fade ${(item.itemTyp !== ItemTyp.Bundle) && 'active show'} `} id='kt_sales_purchase_settings' role='tabpanel'>
              <div className='mb-3 d-flex align-items-center'>
                <input type='checkbox' name='salesFlag' checked={item.salesFlag} onChange={handleInputChange} />
                <LabelWithTooltip
                  className='ms-1'
                  labelText="Sales Item"
                  tooltipText="This item will be enabled for sales transaction"
                />
              </div>
              {item.salesFlag &&
                <>
                  <div className='row mb-7'>
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        <div className='App'>
                          <label className='form-label'>{'Sales Account'.toTranslatedDisplay()}</label>
                          <AccountPicker tabIndex={2}
                            accountCategory={AccountCategory.Income}
                            sysAcctCode='Sale'
                            selectedAccounts={[item.salesAcct || '']}
                            onAccountChange={(selectedOption) =>
                              setItem((prevItem: any) => ({
                                ...prevItem,
                                salesAcct: selectedOption,
                              }))
                            } />
                        </div>
                      </div>
                    </div>
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        <NumberInput
                          label={'Selling Price'.toTranslatedDisplay()}
                          name='salesRate'
                          value={item.salesRate || item.itemRate}
                          onChange={handleInputChange}
                          showCurrency
                        />
                      </div>
                    </div>
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        <label className='form-label'>{'Sales Description'.toTranslatedDisplay()}</label>
                        <input
                          type='text'
                          className='form-control'
                          placeholder={'Sales Item Text'.toTranslatedDisplay()}
                          name='salesDesc'
                          value={item?.salesDesc || item?.itemTxt || ''}
                          onChange={(e) =>
                            setItem((prevItem: any) => ({ ...prevItem, salesDesc: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </>
              }
              <div className='mb-3 d-flex align-items-center'>
                <input type='checkbox' name='purchaseFlag' checked={item.purchaseFlag} onChange={handleInputChange} />
                <LabelWithTooltip
                  className='ms-1'
                  labelText="Purchase Item"
                  tooltipText="This item will be enabled for purchase transaction"
                />
              </div>
              {item.purchaseFlag && <>
                <div className='row mb-7'>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <label className='form-label'>{'Purchase Account'.toTranslatedDisplay()}</label>
                      <AccountPicker tabIndex={2}
                        accountType={AccountType.Expense}
                        sysAcctCode='COGS'
                        selectedAccounts={[item.purchaseAcct || '']}
                        onAccountChange={(selectedOption) =>
                          setItem((prevItem: any) => ({
                            ...prevItem,
                            purchaseAcct: selectedOption,
                          }))
                        } />
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <NumberInput
                        label={'Purchase Rate'.toTranslatedDisplay()}
                        name='purchaseRate'
                        value={item.purchaseRate}
                        onChange={handleInputChange}
                        showCurrency
                      />
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <label className='form-label'>{'Purchase Description'.toTranslatedDisplay()}</label>
                      <input
                        type='text'
                        className='form-control'
                        placeholder={'Purchase Item Text'.toTranslatedDisplay()}
                        name='purchaseDesc'
                        value={item.purchaseDesc || item?.itemTxt || ''}
                        onChange={(e) =>
                          setItem((prevItem: any) => ({ ...prevItem, purchaseDesc: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </>}

            </div>
            {bizSettings?.taxFlag !== false && (
              <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                <div className='row mb-7'>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <LabelWithTooltip
                        className='form-label'
                        labelText='Tax Type'
                        tooltipText="Inclusive: the price you enter already includes tax. Exclusive: tax is calculated on top of the price you enter."
                      />
                      <div className='d-flex align-items-center mt-2'>
                        <SliderSwitch
                          defaultChecked={(item.taxCalcType === TaxCalcType.Inclusive)}
                          onChange={(name, value) =>
                            handleInputChange({
                              target: {
                                name: 'taxCalcType',
                                value: (value) ? TaxCalcType.Inclusive : TaxCalcType.Exclusive
                              }
                            } as React.ChangeEvent<HTMLInputElement>)
                          }
                          name={'taxCalcType'}
                        />
                        <p className='mb-0 ms-2'>{(item.taxCalcType === TaxCalcType.Inclusive) ? 'Inclusive' : 'Exclusive'}</p>
                      </div>
                    </div>
                    {(isCIN || isCNP) &&
                      <div className='mt-8'>
                        <LabelWithTooltip
                          className='form-label'
                          labelText='HSN Code'
                          tooltipText='Harmonized System of Nomenclature code — classifies this item for GST rate determination on tax returns.'
                        />
                        <input
                          type='text'
                          className='form-control'
                          placeholder={'HSN Code'.toTranslatedDisplay()}
                          name='inHsnCode'
                          value={item.inHsnCode || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    }
                  </div>
                  {isCIN ? <>
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        {/* <div className='App'> */}
                        <TaxPicker
                          label={'InterState Tax'.toTranslatedDisplay()}
                          tabIndex={2}
                          selectedTax={item.inIntertax}
                          showIntraStatTax={false}
                          onTaxChange={(selectedTax) =>
                            setItem((prevItem: any) => ({
                              ...prevItem,
                              inIntertax: selectedTax || '',
                            }))
                          } />
                        {/* </div> */}
                      </div>
                      <div className='mb-3'>
                        <LabelWithTooltip
                          className='form-label'
                          labelText='SAC Code'
                          tooltipText='Services Accounting Code — classifies this service item for GST rate determination on tax returns.'
                        />
                        <input
                          type='text'
                          className='form-control'
                          placeholder={'SAC Code'.toTranslatedDisplay()}
                          name='inSacCode'
                          value={item.inSacCode || ''}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        {/* <div className='App'> */}
                        <TaxPicker
                          label={'IntraState Tax'.toTranslatedDisplay()}
                          tabIndex={2}
                          selectedTax={item.inIntratax}
                          showIntraStatTax={true}
                          onTaxChange={(selectedTax) =>
                            setItem((prevItem: any) => ({
                              ...prevItem,
                              inIntratax: selectedTax || '',
                            }))
                          } />
                        {/* </div> */}
                      </div>
                    </div>
                  </> : <>
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        <div className='App'>
                          <TaxPicker
                            label={'Tax'.toTranslatedDisplay()}
                            tabIndex={2}
                            selectedTax={item.taxId}
                            onTaxChange={(selectedTax) =>
                              setItem((prevItem: any) => ({
                                ...prevItem,
                                taxId: selectedTax || '',
                              }))
                            } />
                        </div>
                      </div>
                    </div>
                  </>}

                </div>
              </div>
            )}
            <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
              <div className='row mb-7'>
                <div className='col-md-6'>
                  <div className='mb-3'>
                    <label className='form-label'>{'Do you want to enable inventory for this item?'.toTranslatedDisplay()}</label>
                    <div className='d-flex align-items-center mt-1'>
                      <SliderSwitch
                        defaultChecked={item.inventoryFlag}
                        data-bs-toggle='tab'
                        data-bs-target='#kt_inventory_details'
                        onChange={handleSwitchChange} name={''} />
                      <p className='mb-0 ms-2'>{(item.inventoryFlag) ? 'Enabled' : 'Disabled'}</p>
                    </div>
                  </div>
                </div>
              </div>
              {isInventoryEnabled && (
                <div className='row mb-7' id='kt_inventory_details'>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <NumberInput
                        label={'Opening Stock'.toTranslatedDisplay()}
                        tooltip='Quantity on hand for this item as of the opening stock date, before you started tracking inventory in myBooks.'
                        name='openingStockQuantity'
                        value={item.openingStockQuantity}
                        placeholder={'Opening Stock Quantity'.toTranslatedDisplay()}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className='mb-3'>
                      <NumberInput
                        label={'Reorder Quantity'.toTranslatedDisplay()}
                        tooltip='Stock level at which this item should be flagged as low/out of stock for reordering.'
                        name='reorderQuantity'
                        value={item.reorderQuantity}
                        placeholder={'Minimum Quantity for Reordering'.toTranslatedDisplay()}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <NumberInput
                        label={'Opening Stock Unit Price'.toTranslatedDisplay()}
                        tooltip='Cost per unit used to value the opening stock quantity above on your books.'
                        name='openingStockPrice'
                        value={item.openingStockPrice}
                        placeholder={'Opening Stock Unit Price'.toTranslatedDisplay()}
                        showCurrency
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <label className='form-label'>{'Opening Stock Date'.toTranslatedDisplay()}</label>
                      <DatePicker
                        value={item.openingStockDate}
                        onChange={(value) => {
                          setItem((prevItem) => ({
                            ...prevItem,
                            openingStockDate: value, // Update openingStockDate with the new date value
                          }));
                        }}
                        className='form-control'
                        placeholder={'Pick date'.toTranslatedDisplay()}
                      />
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='mb-3'>
                      <ContactPicker
                        label={'Preferred Vendor'.toTranslatedDisplay()}
                        tooltip='Vendor to pre-select when reordering this item or creating a purchase order.'
                        selectedContact={item.preferredVendor} contactType={ContactType.Vendor} tabIndex={0} onContactChange={function (contactId: string | string[], contactInfo?: Contact): void {
                        setItem((prevItem: any) => ({
                          ...prevItem,
                          preferredVendor: contactId,
                        }))
                      }} />

                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className='tab-pane fade mb-5' id='kt_tab_pane_4' role='tabpanel'>
              <div className='row mb-7'>
                {
                  fieldConfig.map((fieldDetail, index) => (
                    <div className='col-md-3'>
                      <div className='mb-3'>
                        <div key={index} className="form-group">
                          <label className='form-label' htmlFor={fieldDetail.fieldName}>{fieldDetail.fieldHeading || fieldDetail.fieldName}</label>
                          <input
                            type={fieldDetail.fieldType || 'text'}
                            id={fieldDetail.fieldName}
                            name={fieldDetail.fieldName}
                            value={item.customFields?.find(field => field.fieldName === fieldDetail.fieldName)?.fieldValue || ''}
                            className="form-control"
                            onChange={handleCustomFields}
                          />

                        </div>
                      </div>
                    </div>
                  ))
                }
              </div>


              <a href="#" onClick={handleAddFieldModalToggle} className="btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary">{'Add Custom Fields'.toTranslatedDisplay()}</a>
              {fieldConfig.length > 0 &&
                <a href="#" onClick={handleEditFieldModalToggle} className=" ms-2 btn btn-outline btn-outline-dashed btn-outline-secondary btn-active-light-secondary">{'Modify Custom Fields'.toTranslatedDisplay()}</a>
              }
            </div>
            <div
              className={`tab-pane fade ${item.itemTyp === ItemTyp.Bundle && "active show"
                } mb-5`}
              id="kt_tab_pane_5"
              style={{ zIndex: 1050 }}
              role="tabpanel"
            >
              <div className='mb-5'>
                <div className='mb-3 d-flex align-items-center'>
                  <input type='checkbox' name='salesFlag' checked={item.salesFlag} onChange={handleInputChange} />
                  <LabelWithTooltip
                    className='ms-1'
                    labelText="Available for Sales"
                    tooltipText="This bundle will be available for sales transactions"
                  />
                </div>
                <div className='mb-3 d-flex align-items-center'>
                  <input type='checkbox' name='purchaseFlag' checked={item.purchaseFlag} onChange={handleInputChange} />
                  <LabelWithTooltip
                    className='ms-1'
                    labelText="Available for Purchase"
                    tooltipText="This bundle will be available for purchase transactions"
                  />
                </div>
              </div>
              <div>
                {item.subItems?.map((subItem, index) => (
                  <div key={index} className="d-flex my-2">
                    <div className="col-md-3">
                      <ItemPicker
                        placeHolder="Sub Item"
                        selectedItem={subItem.itemId}
                        onItemChange={(selectedItem: Item | Item[]) => {
                          handleSubItemChange(index, {
                            itemId: Array.isArray(selectedItem) ? selectedItem[0]?.itemId : selectedItem.itemId,
                          });
                        }}
                      />
                    </div>
                    <div className="ms-3">
                      <NumberInput
                        name=""
                        value={subItem.itemQty}
                        placeholder={'Quantity'.toTranslatedDisplay()}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                          parsedValue: number | undefined
                        ) => {
                          handleSubItemChange(index, { itemQty: parsedValue });
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="ms-3 btn p-2 btn-light-danger"
                      onClick={() => handleDeleteRow(index)}
                    >
                      <KTIcon
                        className="fs-1 "
                        iconType="duotone"
                        iconName="trash"
                      />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-light-primary my-2"
                  onClick={handleAddBundleItemRow}
                >{'Add Bundled Item'.toTranslatedDisplay()}</button>
              </div>
            </div>

            <CustomModal
              title={'Add Custom Field'.toTranslatedDisplay()}
              isVisible={showAddFieldModal}
              onClose={handleAddFieldModalClose}
              body={<AddFieldConfigurator
                key={showAddFieldModal ? 'open' : 'closed'}
                objectName={'Item'}
                onSave={function (fieldConfigs: FieldConfig[], save: boolean): void {}}
                onClose={function (fieldConfigUpdate?: boolean | undefined): void {
                  handleAddFieldModalClose()
                }}
                pushFieldConfog={(configs) => setFieldConfigs(configs)}
              />}
              size="small"
              id="addCustomFieldModal"
            />
            <CustomModal
              title={'Modify Custom Field'.toTranslatedDisplay()}
              isVisible={showEditFieldModal}
              onClose={handleEditFieldModalClose}
              body={<EditFieldConfigurator objectName={'Item'} onClose={handleEditFieldModalClose} onSave={function (fieldConfigs: FieldConfig[], save: boolean): void {
                throw new Error('Function not implemented.');
              }} />}
              size="small"
              id="addCustomFieldModal"
            />
            <ButtonWithLoader shortcutTip='Ctrl+S / ⌘+S' shortcutKeys={
              ['ctrl+s', 'meta+s']
            } isLoading={isLoading} onClick={handleSubmit} >
              {'Save'.toTranslatedDisplay()}
            </ButtonWithLoader>
            <button type='button' className='btn btn-secondary ms-2' onClick={handleExit}>{'Cancel'.toTranslatedDisplay()}</button>
          </div>
        </div>
      </div>
      <CustomPrompt
        isVisible={isPromptVisible}
        onClose={() => setPromptVisible(false)}
        onConfirm={handleConfirmExit}
        message={'You have unsaved changes. Do you really want to leave?'.toTranslatedDisplay()}
        title={'Unsaved Changes'.toTranslatedDisplay()}
        PrimaryText={'OK'.toTranslatedDisplay()}
        secondaryText={'Cancel'.toTranslatedDisplay()}
      />
    </Content>

  );
}

